/* eslint-disable react/jsx-props-no-spreading */
import { FC, useEffect } from 'react'
import ReactGA from 'react-ga4'
import { matchRoutes, Navigate, useParams } from 'react-router-dom'
import { useAppSelector } from '../hooks'
import type { IRoutes } from '../interfaces/config'
import type { ITemplate } from '../interfaces/templates'
import Default from './Default'

/**
 * Where the guard below sends a shopper with no attempt behind them: the
 * storefront's own orders route when it has authored one, its cart otherwise.
 *
 * `routes` is the SCS-authored table as `routes/index.tsx` receives it, and
 * `matchRoutes` is the function that table is already matched with, so this
 * asks the only question that matters — *would the target render anything?* —
 * rather than pattern-matching the route string. It is typed as present on
 * `IConfig` and is nevertheless optional at runtime (that file guards
 * `config.routes &&` before mapping it), which is why both it and the params
 * are read defensively. The reasoning for preferring orders, and for the
 * cart being the fallback rather than the choice, is at the call site.
 */
const resolveExit = (
  routes: IRoutes[] | undefined,
  slug?: string,
  sessionId?: string,
) => {
  const ordersPath = `/${slug}/${sessionId}/orders`
  const authored = routes?.map(({ route }) => ({ path: route })) ?? []

  return matchRoutes(authored, ordersPath)
    ? ordersPath
    : `/${slug}/${sessionId}/cart`
}

/**
 * Order Confirmation screen.
 *
 * There is no route for it in code: cinnamon builds its routes from the SCS
 * config's own `routes` array, and `defaultConfig` ships none for this — so
 * `CinnamonPaths.ORDER_CONFIRMATION` is the path an author is expected to
 * register, not one that exists, and no code navigates to it either. The
 * screen is therefore reached only once a storefront authors both the
 * container and a `pushAfter` onto it — which is no longer hypothetical:
 * `mondelez-mx-b2b-ng`'s live SCS config authors exactly that (a config-side
 * fact, not one this repo can show), and COM-858 is what made the authored
 * push actually fire.
 *
 * Thin like `OrderDetail` and `OrderHistory` beside it: fire analytics,
 * delegate rendering to `Default`. `OrderConfirmationPanel`, its state copy
 * and its custom slots are authored in SCS config and resolve through
 * `importRoute`, so no layout belongs here — and the props the config cannot
 * express are injected in `useCinnamonProps`, not passed down.
 *
 * **It deliberately fetches nothing.** The other two screens call a hook on
 * mount because they are reads. This one is the tail of a *write* that
 * `useOrderConfirmation` already owns, started from the cart CTA through
 * `useCreateOrder`: the mutation, the confirmation, the poll and the stephook
 * all run there, and a settled order is in `ordersSlice.created` before this
 * screen mounts.
 *
 * Note this screen already *has* an instance of that hook — every container
 * does, via `Default` → `EventManager` → `useCreateOrderHook` — and it is
 * inert because nothing here calls `createAndConfirmOrder`. What calling the
 * hook in this file would add is a second **chain**: another instance with its
 * own poll timer and request token in `useRef`, writing the same slice on its
 * own schedule. That is COM-741's defect class, and the reason COM-779 put the
 * detail refresh behind a nonce rather than a second hook. The nonce work for
 * this screen should drive the instance `EventManager` already mounts.
 *
 * Two consequences worth naming, both recorded in `.claude/CLAUDE.md`:
 *
 * - **Reaching this route no longer kills the attempt (COM-818).**
 *   `usePushView` is `navigate()` and `<Routes>` mounts one container at a
 *   time, so arriving here unmounts the cart's `EventManager` — the instance
 *   that owns the in-flight attempt. It used to have a cleanup effect, and
 *   both halves of it were harmful: bumping the request token discarded a
 *   `createOrder` that was merely slower than the unmount, and stopping the
 *   timer stranded a poll chain that had already armed. That was the flow
 *   rather than an edge of it, and it left `creating` or `confirming` on screen
 *   for good. There is no cleanup now: Redux lives above `<Routes>`, so a late
 *   outcome still lands here, and an orphaned chain keeps ticking until it
 *   settles or spends its own `timeout` budget and reports `unknown`. Note
 *   which unmount that was: until COM-858 the route table was gated on the
 *   global `loading` flag, so the `CREATE_ORDER` spinner de-registered every
 *   route and deleted the cart container before any navigation happened — on
 *   `main` nothing navigated here at all. COM-858 narrows the gate, so arriving
 *   here by `pushAfter` is the live mechanism from now on. Earlier revisions of
 *   this comment claimed the synchronous path was unaffected and that `unknown`
 *   was unreachable; both were wrong, and a third claimed navigation caused the
 *   unmount.
 * - **A direct link used to show a spinner over no order, and the guard below
 *   is the fix (COM-819).** `creationStatus` stays `idle`, which maps to the
 *   panel's in-flight `creating`, and that was a dead end: not for want of
 *   copy — `defaultTexts` ships `orderConfirmation.returnLabel` in all seven
 *   locales as of #730 — but because the panel gates its exit on the
 *   `returnToConversationLabel` **prop** and nothing forwards a value to it
 *   (`ORDER_CONFIRMATION_RETURN_LABEL` exists in `defaultConstants.ts` and is
 *   referenced by no `defaultProps`, so the token resolves nowhere). The panel
 *   marks a missing exit with `data-exit="unavailable"`, but only on a
 *   terminal status: the gate is `!inFlight && !showsReturn`, where
 *   `showsReturn` is `!!returnToConversationLabel &&
 *   shows('returnToConversation')` — so the label is not the only half, a
 *   tenant can switch the exit off through `fieldsToDisplay` as well — and the
 *   in-flight case was therefore unmarked as well as inescapable.
 *   `hooks/orders/useOrderActions.ts` states the same gate, and an earlier
 *   revision of this comment named a `!hasExit` that is an identifier of
 *   neither repo.
 *
 *   **The guard below covers one of the two config shapes that reach the
 *   panel.** It runs only where `BaseContainer` picked this template, i.e. on
 *   a container declaring `type: 'orderConfirmation'`. The panel is resolved
 *   by `importRoute` through `useCinnamonProps`' `PANEL_PROP_INJECTORS`, which
 *   never consults the type, and `type` is optional on `IContainersBase` — so
 *   a `default`-type container holding an `OrderConfirmationPanel` still
 *   reproduces COM-819, unguarded (`containers/tests/BaseContainer.test.tsx`
 *   pins that fall-through). Not hypothetical: `mondelez-mx-b2b-ng` does
 *   declare the type on its confirmation container, so the observed case is
 *   genuinely fixed, but 6 of its 15 containers declare no type at all.
 */
const OrderConfirmation: FC<ITemplate> = ({ ...props }) => {
  const { slug, sessionId } = useParams()
  const creationStatus = useAppSelector(
    (state) => state.ordersSlice.creationStatus,
  )
  const created = useAppSelector((state) => state.ordersSlice.created)
  const routes = useAppSelector((state) => state.defaultSlice.config?.routes)

  /**
   * No attempt exists, so there is nothing for this screen to be about.
   *
   * `idle` is the slice's resting state, but it is the *absence of an order*
   * that says no attempt was ever made. Two states have to survive this guard,
   * each broken by a different half of it — and only one of the two can happen
   * in the running app:
   *
   * - **`creating` with `created: null` is a real attempt in flight** and must
   *   keep its spinner. This is the ordinary flow: the cart dispatches
   *   `setOrderCreating` before `createOrder` goes out. **`!created` alone is
   *   what breaks it**, reading a live attempt as no attempt at all — see
   *   "keeps the spinner for an attempt that is genuinely in flight" in the
   *   suite.
   * - **`idle` alongside a `created` order is a settled one** the panel should
   *   still render. **The status alone is what breaks it**, bouncing a shopper
   *   off an order that exists — see "stays on the screen when the status is
   *   idle but an order exists".
   *
   *   **This second one is defensive, not a live shopper state, and the
   *   conjunct is still right.** The only reducer that returns
   *   `creationStatus` to `'idle'` is `clearOrderCreation`
   *   (`redux/slices/orders/orders.ts`), which nulls `created` in the same
   *   transition, and `ordersSlice` has no persistence to rehydrate one
   *   without the other — so the pair arises only from a test's
   *   `preloadedState`. It is kept because the conjunct costs nothing and the
   *   mutant is real: a reducer that cleared the status without the order, or
   *   persistence added later, would put a settled order behind an `idle`
   *   status and this guard would bounce a shopper who had just ordered. Of
   *   the two halves it is the one a reader is most likely to delete as
   *   redundant, which is why it is labelled here rather than only pinned in a
   *   test.
   *
   * An earlier revision of this comment mapped the two halves to the wrong
   * failure modes, the tests having them the right way round.
   */
  const hasNoAttempt = creationStatus === 'idle' && !created

  useEffect(() => {
    // Not a screen the shopper reaches, so not a pageview. The redirect below
    // is committed in the same render, and whichever screen it lands on fires
    // its own event — the cart's `view_cart` from `runOnLoad`, or
    // `OrderHistory`'s own `pageview` from the same effect shape as this one.
    if (hasNoAttempt) return
    ReactGA.send({ hitType: 'pageview', page: `/${props.container.uid}` })
    window.fbq('track', 'PageView', { page: `/${props.container.uid}` })
  }, [])

  /*
   * **An authored orders route if the storefront has one, the cart
   * otherwise**, and the config surface that choice needs is still **none**:
   * the fallback is the one route every storefront has.
   *
   * The destination is chosen for the shopper who *did* order, not for the
   * stranger who pasted the URL. `pushView` is a push, so after a real order
   * the confirmation URL **is** the address bar, and `ordersSlice` has no
   * persistence — `redux/store.tsx` is a bare `configureStore` with no
   * `redux-persist`, and `preloadedState` is test-only. A pull-to-refresh
   * inside a WhatsApp webview therefore re-boots at `idle`/`null`, which this
   * guard cannot tell apart from a paste, and it has to send them somewhere.
   * Sending them to the cart their own order has just emptied — with
   * `replace`, so the confirmation URL is gone from the history and they can
   * neither retry the reload nor press Back — trades the pre-fix stuck spinner
   * for a loss of *recoverability*: that spinner was broken too, but it kept
   * the URL. Their order history is the one screen where the content of this
   * one still exists.
   *
   * The orders route is SCS-authored and may be absent, and an earlier
   * revision took that as a reason to rule it out. It was a reason to **test**
   * for it. The test is `matchRoutes` against the authored table — the same
   * function `<Routes>` matches with, on the same array `routes/index.tsx`
   * feeds it, so a pattern cannot parse one way here and another way there,
   * and an absent or empty `routes` simply matches nothing. It is not
   * equivalent to `route.endsWith('/orders')`, which also accepts a route like
   * `/:slug/:sessionId/:tab/orders` that the target path does **not** match:
   * that redirect would land on `NotFound`, the failure this guard exists to
   * remove. `defaultConfig` authors no orders route, so a storefront that
   * configures nothing keeps the cart.
   *
   * An in-panel empty state and the panel's own exit (gated on
   * `returnToConversationLabel`, which no `defaultProps` binds) stay
   * unavailable by construction: a storefront that has not authored this
   * screen is exactly the one that reaches it, and both need authored copy.
   *
   * **One `<Navigate>` with the destination computed above it**, rather than a
   * branch returning one each. Two of them would be two places `replace` could
   * be dropped from, and the security note below applies to both targets
   * equally.
   *
   * `CinnamonPaths.CART`/`ORDERS` are deliberately not used: each is
   * `'/{{params.slug}}/{{params.sessionId}}/cart'`, a config *template*
   * resolved by `processConfig`, and handing it to `navigate` routes to the
   * literal string. The path is built from `useParams` instead — the
   * `navigate` call in `useReorder` is the sibling precedent for the shape
   * (`/<slug>/<session>/cart`), and the *source* differs from its Redux one on
   * purpose. `defaultSlice.sessionId` is written by a `Config` effect
   * that runs after the first render in which the route table exists, so a
   * render-time read of it can be `undefined`; the route match cannot be. The
   * names are the ones the config DSL itself requires — `RouterParams` feeds
   * `useParams()` into `defaultSlice.params`, which is what every
   * `{{params.slug}}` in a storefront's config resolves against — so a route
   * that named them otherwise would have broken its own tokens first. They
   * are also basename-relative on both sides, so the ephemeral and language
   * prefixes need no handling here.
   *
   * **This is the repo's first navigation target built from raw `useParams()`
   * output.** Building a `/${slug}/${sessionId}/…` path is precedented five
   * times — `containers/404.tsx` and `hooks/filters/useFilters.tsx` (no
   * trailing segment), `hooks/orders/useOrderActions.ts` (twice, to
   * `/orders/…`) and `hooks/orders/useReorder.ts` (the only other `/cart`
   * target) — but every one of those reads its two values from **Redux**,
   * which is the half that matters. Anyone copying this site inherits the
   * `replace` below as a requirement, not a preference.
   *
   * **`replace` rather than a push, and it is open-redirect containment as
   * well as a Back-button fix.** Both reasons hold; the second is the one that
   * must not be traded away.
   *
   * The Back-button half: the entry being left is one the shopper cannot
   * usefully return to, so a push would make Back bounce off this guard and
   * land on the cart again — a trapped Back button in place of a trapped
   * screen.
   *
   * The security half, verified in the installed
   * `react-router-dom@6.28.0` / `@remix-run/router@1.21.0` rather than
   * recalled. `slug` can contain a `/`: `matchRoutes` decodes the pathname
   * (`decodePath` re-escapes any `/` that decoding produced back to `%2F`) and
   * `matchPath` reverses that on capture (`(value || '').replace(/%2F/g,
   * '/')`), so `/%2f%2fevil.com/sess/order-confirmation` matches this route
   * with `slug === '//evil.com'` and `to` becomes `///evil.com/sess/cart`.
   * What stops that being an open redirect is the *navigation method*:
   * `history.replace` calls `globalHistory.replaceState` bare, so the
   * browser's same-origin check throws `SecurityError` and nothing navigates
   * — the throw lands in the `<ErrorBoundaries>` that `routes/index.tsx` wraps
   * every route element in, and the crafter breaks only their own tab.
   * `history.push` wraps `pushState` in a try/catch that rethrows only
   * `DataCloneError` and otherwise falls through to
   * `window.location.assign(url)`, which *does* navigate cross-origin. So
   * swapping this for a push, or for a bare `navigate(path)`, is a security
   * regression rather than a style change.
   *
   * A second bound exists on some deployments and covers the same input by a
   * different route: when `basename !== '/'` — ephemeral builds keyed by
   * commit SHA, or a language prefix — `useNavigate` prepends it through
   * `joinPaths`, which is `paths.join('/').replace(/\/\/+/g, '/')` and
   * collapses every run of slashes, flattening `///evil.com/...` to a
   * same-origin 404. The dangerous-looking form survives only where
   * `basename === '/'`, which is where `replaceState`'s check catches it.
   *
   * Deliberately **no sanitisation or allowlist on `slug`**: the containment
   * above is real, and percent-encoding the param would change the resolved
   * path for every legitimate storefront whose slug is a plain segment.
   *
   * Deliberately **no fallback for absent params** either, and the sibling
   * precedent does not transfer. `useOrderActions.ts:77` guards
   * `if (!orderId || !slug || !sessionId) return`, which is right there
   * because it is an event handler: returning leaves the shopper on the screen
   * they are already looking at. Here there is no such screen — the two
   * available fallbacks are rendering the panel (the COM-819 defect itself) or
   * rendering nothing (a blank dead end). A config that reaches this has
   * already broken its own `{{params.*}}` tokens.
   *
   * **What that redirect actually does is worse than `NotFound`**, and an
   * earlier revision of this comment claimed it landed there "which at least
   * has a working exit". It does not: `:slug` matches the literal segment
   * `undefined`, verified against the installed `@remix-run/router@1.21.0`
   * (`/undefined/undefined/cart` matches `/:slug/:sessionId/cart` with
   * `slug: 'undefined'`), so the **real cart renders and looks fine**.
   * `RouterParams` then writes `slug: 'undefined'` into
   * `defaultSlice.params`, so every `{{params.slug}}` in that storefront's
   * config addresses a shadow URL space; and `containers/Config.tsx` re-reads
   * `window.location.pathname` on every render, so its first effect sees
   * `slug !== slugFromUrl`, refetches the config for slug `"undefined"`, SCS
   * 404s and the shopper gets the global error screen mid-checkout. Left to
   * fail rather than papered over here, but do not read it as contained.
   * (`404.tsx`'s blank branch is not reachable from here either way: its
   * `storeName`/`sessionId` come from `getUrlParams`' positional split of the
   * pathname rather than from route params, and are populated before any route
   * element mounts.)
   */
  if (hasNoAttempt) {
    return <Navigate to={resolveExit(routes, slug, sessionId)} replace />
  }

  return <Default {...props} />
}

export default OrderConfirmation
